import React, { useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { ScansTabContent } from '../components/certificateTabs/tabs/ScansTabContent'
import { Footer } from 'components/Footer'
import { CertificateSubView } from './CertificateSubView'

/**
 * VERA-315 Phase 4 — the Scans tab, as its own screen.
 *
 * The view-mode state (map vs list) lives here now rather than on the
 * certificate page, which is where it belonged all along: its only consumer was
 * the footer's sticky rule, and both are on this screen now. The footer sticks
 * for the LIST and not the map — the map does not scroll, so pinning the
 * attribution over it would only eat map area (VERA-476, VERA-978).
 */
export const ScansSubView = () => {
  const { certificateId } = useParams()
  const [viewMode, setViewMode] = useState<'map' | 'list'>('map')

  /**
   * VERA-315 — the scan this screen was opened FOR, when it was opened from
   * something that names one (the certificate page's suspicious-activity card).
   *
   * Router STATE, not a query param, and that is forced rather than chosen: this
   * app runs a `MemoryRouter` (see the note on `window.location.reload()` in
   * `CertificatePage`), so there is no address bar for a param to survive in and no
   * shareable URL for it to be part of. `openSubView` already carries `slideIn`
   * through the same channel.
   *
   * Read on every render rather than captured once. `location.state` is stable for
   * the life of an entry, so re-reading it costs nothing and avoids a stale capture
   * if the same screen is re-entered with a different scan.
   */
  const location = useLocation()
  const focusScanId = (location.state as { focusScanId?: string } | null)?.focusScanId ?? null

  return (
    <CertificateSubView
      title='Scans'
      footerSticky={viewMode === 'list'}
      // Map mode only: the map runs to the bottom edge, so the attribution goes
      // above it. The LIST ends normally and keeps the footer beneath it.
      attributionAbove={viewMode === 'map'}
    >
      {certificate => (
        <ScansTabContent
          certificateId={certificateId || ''}
          isShowScanHistory
          onViewModeChange={setViewMode}
          focusScanId={focusScanId}
          // This is a screen, not a panel in a scrolling page, so the map takes
          // the viewport and bleeds to both edges.
          fillViewport
          // Placed by the content, on the map's top edge — see the prop's note.
          attribution={<Footer hidePoweredBy={certificate?.hide_powered_by} />}
        />
      )}
    </CertificateSubView>
  )
}
